import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react'
import {
  getTodayIso,
  addDaysToISODate,
  isoDateToDisplayDate,
  isoDateToWeekday,
  getLeastDistanceWithValue,
} from '@/utils'

const TOTAL_DAYS = 60
const CHIP_WIDTH = 106
const GAP_WIDTH = 1.9 * 8
const SCROLL_CHIPS = 3
const chipTotalWidth = CHIP_WIDTH + GAP_WIDTH

export const useDateScroll = (id, formData, dataMap, onSelect, onScroll) => {
  const maxDistance = formData?.distance
  const userLatitude = formData?.address?.latitude
  const userLongitude = formData?.address?.longitude

  const containerRef = useRef(null)
  const hasInitialized = useRef(false)
  const [currentIndex, setCurrentIndex] = useState(() => Math.floor(365 / 2))
  const [isReady, setIsReady] = useState(false)
  const [leastDistanceDates, setLeastDistanceDates] = useState([])
  const [startIso, setStartIso] = useState(formData?.date || getTodayIso())
  const [selectedId, setSelectedId] = useState()

  useEffect(() => {
    if (!formData?.date || hasInitialized.current) return
    setStartIso(formData?.date)
    hasInitialized.current = true
  }, [formData?.date])

  useEffect(() => {
    if (!formData?.date) return
    setSelectedId(formData?.date)
  }, [formData?.date])

  const chips = useMemo(() => {
    const HALF = Math.floor(TOTAL_DAYS / 2)

    let currentIso = addDaysToISODate(startIso, -HALF)

    const arr = []
    let minDistance = Infinity
    const minDistanceIsos = []

    for (let i = 0; i < TOTAL_DAYS; i += 1) {
      const matched = dataMap[currentIso]
      const distanceData = getLeastDistanceWithValue(
        matched,
        userLatitude,
        userLongitude
      )

      const isWithinMaxDistance =
        maxDistance === 'any' || distanceData.value <= maxDistance

      arr.push({
        id: currentIso,
        index: i,
        weekday: isoDateToWeekday(currentIso),
        dateDisplay: isoDateToDisplayDate(currentIso),
        text1: isoDateToDisplayDate(currentIso),
        text2: isWithinMaxDistance ? distanceData.text : '',
        item: matched,
      })

      if (isWithinMaxDistance) {
        if (distanceData.value < minDistance) {
          minDistance = distanceData.value
          minDistanceIsos.length = 0
          minDistanceIsos.push(currentIso)
        } else if (
          distanceData.value === minDistance &&
          distanceData.value !== Infinity
        ) {
          minDistanceIsos.push(currentIso)
        }
      }
      currentIso = addDaysToISODate(currentIso, 1)
    }

    setLeastDistanceDates(minDistanceIsos)
    return arr
  }, [dataMap, startIso, maxDistance, userLatitude, userLongitude])

  useLayoutEffect(() => {
    const node = containerRef.current
    if (!node) return
    const targetId = startIso
    const chipEl = node.querySelector(`[data-testid="${id}-chip-${targetId}"]`)
    if (!chipEl) return

    let raf1
    let raf2

    const centerChip = () => {
      const chipRect = chipEl.getBoundingClientRect()
      const nodeRect = node.getBoundingClientRect()
      const chipCenterX =
        chipRect.left - nodeRect.left + node.scrollLeft + chipRect.width / 2
      const targetLeft = chipCenterX - node.clientWidth / 2
      node.scrollLeft = Math.max(targetLeft, 0)
      setIsReady(true)
    }

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(centerChip)
    })

    return () => {
      if (raf1) cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
    }
  }, [id, chips.length, startIso])

  const handleScrollPrefetch = useCallback(() => {
    const node = containerRef.current
    if (!node || !onScroll) return

    // Calculate which chip is currently at the center of the viewport
    const scrollLeft = node.scrollLeft
    const containerWidth = node.clientWidth
    const centerPosition = scrollLeft + containerWidth / 2

    // Find the index of the chip at center
    const centerChipIndex = Math.round(centerPosition / chipTotalWidth)
    const boundedCenterIndex = Math.max(
      0,
      Math.min(centerChipIndex, chips.length - 1)
    )

    // Update current index
    setCurrentIndex(boundedCenterIndex)

    // Determine scroll direction based on previous index
    const scrollDirection = boundedCenterIndex > currentIndex ? 'right' : 'left'
    const prefetchOffset = scrollDirection === 'left' ? -4 : 4
    const prefetchIndex = boundedCenterIndex + prefetchOffset

    const prefetchChip = chips.find(chip => chip.index === prefetchIndex)
    if (prefetchChip) {
      onScroll(prefetchChip)
    }
  }, [chips, currentIndex, onScroll])

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    let scrollTimeout = null

    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        handleScrollPrefetch()
      }, 150) // Wait 150ms after scroll stops
    }

    node.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout)
      node.removeEventListener('scroll', handleScroll)
    }
  }, [handleScrollPrefetch])

  const scrollByAmount = dir => {
    const node = containerRef.current
    if (!node) return
    const delta = dir === 'left' ? -1 : 1
    const amount = delta * (CHIP_WIDTH + GAP_WIDTH) * SCROLL_CHIPS
    try {
      node.scrollBy({ left: amount, behavior: 'smooth' })
    } catch {
      node.scrollLeft = node.scrollLeft + amount
    }
  }

  const handleSelect = chip => {
    setSelectedId(chip.id)
    setCurrentIndex(chip.index)

    if (onSelect) onSelect(chip)
    // if (onSelect) {
    //   requestAnimationFrame(() => {
    //     onSelect(chip)
    //   })
    // }
  }

  return {
    chips,
    isReady,
    selectedId,
    containerRef,
    leastDistanceDates,
    handleSelect,
    scrollByAmount,
  }
}
